/* eslint-disable fp/no-loops */
/* eslint-disable fp/no-mutating-methods */
/* eslint-disable no-restricted-syntax */
export interface EquationPartsModel {
  integer: number
  variable?: string
}

export interface LatexEquationPartModel {
  integer: number
  variable?: string
  exponent?: number
}

/** Renders equation parts (sign / coefficient / variable / exponent) as LaTeX, e.g. `3x^{2} - y`. */
export function equationPartsToLatex(parts: LatexEquationPartModel[]): string {
  return parts
    .map(({ exponent, integer, variable }, i) => {
      const sign = i > 0 || integer < 0 ? (integer < 0 ? ' - ' : ' + ') : ''
      const coefficient = !exponent || Math.abs(integer) > 1 ? `${Math.abs(integer)}` : ''
      const variablePart = (exponent ?? 0) > 0 ? ((exponent ?? 0) > 1 ? `${variable}^{${exponent}}` : variable) : ''
      return `${sign}${coefficient}${variablePart}`
    })
    .join('')
}
/**
 * It takes a equation string and returns an array of equation part objects
 * @param {string} equationString - string - the equation string to be converted to equation parts
 * @returns An array of objects that contain the variable and integer of the equation.
 */
export function equationStringToSimplifiedParsedString(equationString: string): string {
  try {
    // first pass - break operator, integer, variables into parts, will join them later when necessary
    const equationArray = parseEquationString(equationString)
    // second pass - make meaningful equation parts from all parts
    const equationParts = parseEquationArrayToEquationParts(equationArray)
    // simplify equation parts
    const simplifiedEquationParts = simplifyEquationParts(equationParts)
    // convert equation parts to string
    return equationPartsToString(simplifiedEquationParts)
  } catch (error) {
    return ''
  }
}

/**
 * It takes a equation string and returns an array of sorted(by variable) equation string
 * @param {string} equationString - string - the equation string to be converted to equation parts
 * @returns An array of objects that contain the variable and integer of the equation.
 */
export function equationStringToSortedParsedString(equationString: string): string {
  try {
    // first pass - break operator, integer, variables into parts, will join them later when necessary
    const equationArray = parseEquationString(equationString)
    // second pass - make meaningful equation parts from all parts
    const equationParts = parseEquationArrayToEquationParts(equationArray)
    // sort equation parts
    const sortedEquationParts = sortEquationParts(equationParts)
    // convert equation parts to string
    return equationPartsToString(sortedEquationParts)
  } catch (error) {
    return ''
  }
}

/**
 * It takes a string, removes all white spaces, and breaks it into parts of numbers, variables, and
 * operators
 * @param {string} equationString - The string that represents the equation.
 * @returns An array of strings and numbers
 */
function parseEquationString(equationString: string): Array<string | number> {
  equationString = equationString.replaceAll(' ', '') // remove all white spaces
  const equationParts = []
  let numberArray = []
  let variableArray = []
  // break operator, integer, variables into parts
  for (const char of equationString) {
    if (char >= '0' && char <= '9') {
      // handle for number
      if (variableArray.length > 0) {
        equationParts.push(variableArray.join(''))
        variableArray = []
      }
      numberArray.push(char)
    } else {
      // handle for non number characters
      if (numberArray.length > 0) {
        equationParts.push(parseInt(numberArray.join('')))
        numberArray = []
      }
      if (char >= 'a' && char <= 'z') {
        // handle for variables
        variableArray.push(char)
      } else if (char === '-' || char === '+' || char === '*') {
        // handle for operator
        if (variableArray.length > 0) {
          equationParts.push(variableArray.join(''))
          variableArray = []
        }
        if (char !== '*') equationParts.push(char)
      } else {
        // throw error if unknown character found
        throw new Error('Unknown character')
      }
    }
  }
  if (variableArray.length > 0) {
    equationParts.push(variableArray.join(''))
    variableArray = []
  }
  if (numberArray.length > 0) {
    equationParts.push(parseInt(numberArray.join('')))
    numberArray = []
  }
  return equationParts
}

/**
 * It takes an array of strings and numbers representing individual number, variable, operators and returns an array of objects with a variable and
 * integer property
 * @param equationArray - An array of strings and numbers that represent the equation.
 * @returns An array of objects representing EquationParts with two properties: variable and integer.
 */
function parseEquationArrayToEquationParts(equationArray: Array<string | number>): EquationPartsModel[] {
  const equationParts: EquationPartsModel[] = []
  let constant = 1
  let variable = ''
  let operator = '+'
  for (let i = 0; i < equationArray.length; i++) {
    const part = equationArray[i]
    if (part === '+' || part === '-') {
      if (i > 0) {
        equationParts.push({
          variable: variable.length > 0 ? variable.split('').sort().join('') : undefined,
          integer: operator === '+' ? constant : -1 * constant,
        })
      }
      operator = part
      constant = 1
      variable = ''
    } else if (typeof part === 'number') {
      constant *= part
    } else {
      variable += part
    }
  }
  if (variable.length > 0 || constant > 0) {
    equationParts.push({
      variable: variable.length > 0 ? variable.split('').sort().join('') : undefined,
      integer: operator === '+' ? constant : -1 * constant,
    })
  }
  return equationParts
}

/**
 * It takes an array of EquationPartsModel objects and returns a string that represents the equation
 * @param {EquationPartsModel[]} equationParts - EquationPartsModel[]
 * @returns A string of the equation parts
 */
export function equationPartsToString(equationParts: EquationPartsModel[]): string {
  let equationString = ''
  equationParts.forEach((part, index) => {
    equationString += `${index > 0 || part.integer < 0 ? (part.integer > 0 ? ' + ' : ' - ') : ''}${
      Math.abs(part.integer) !== 1 || part.variable === undefined ? Math.abs(part.integer) : ''
    }${part.variable !== undefined ? part.variable : ''}`
  })
  return equationString
}

/**
 * Sorts the equation parts by variable
 * @param equationParts - An array of EquationPartsModel objects
 * @returns An array of EquationPartsModel objects
 */
function sortEquationParts(equationParts: EquationPartsModel[]): EquationPartsModel[] {
  const _sortedEquationParts: EquationPartsModel[] = equationParts.map((part) => ({
    integer: part.integer,
    variable: part.variable === undefined ? part.variable : part.variable.split('').sort().join(''),
  }))
  _sortedEquationParts.sort((a, b) => {
    const aVariable = a.variable ?? ''
    const bVariable = b.variable ?? ''
    return bVariable.length - aVariable.length || aVariable.localeCompare(bVariable)
  })
  return _sortedEquationParts
}

/**
 * It takes an array of objects with two properties, integer and variable, and returns an array of
 * objects with the same two properties, but with the integer property summed up for each unique
 * variable and the array sorted by variable
 * @param {EquationPartsModel[]} equationParts - An array of EquationPartsModel objects.
 * @returns An array of EquationPartsModel objects.
 */
export function simplifyEquationParts(equationParts: EquationPartsModel[]): EquationPartsModel[] {
  const result: EquationPartsModel[] = []
  const _sortedEquationParts: EquationPartsModel[] = equationParts.map((part) => ({
    integer: part.integer,
    variable: part.variable === undefined ? part.variable : part.variable.split('').sort().join(''),
  }))
  _sortedEquationParts.sort((a, b) => {
    const aVariable = a.variable ?? ''
    const bVariable = b.variable ?? ''
    return bVariable.length - aVariable.length || aVariable.localeCompare(bVariable)
  })
  result[0] = _sortedEquationParts[0]
  for (let i = 1; i < _sortedEquationParts.length; i++) {
    if (_sortedEquationParts[i].variable === result[result.length - 1].variable) {
      result[result.length - 1].integer += _sortedEquationParts[i].integer
    } else {
      result[result.length] = _sortedEquationParts[i]
    }
  }
  return result
}

/**
 * Removes points and parenthesis from a string and make lowercase if necessary
 * @param value input string
 * @param convertLowerCase should convert to lowercase or not
 * @returns transformed string
 */
export function removePointsAndParenthesis(value: string, convertLowerCase = true): string {
  let _value = value
  if (convertLowerCase) {
    _value = _value.toLowerCase()
  }
  return _value.replace(/[,.)(]/g, '')
}
